import { auth } from "@lumberset/api-client";
import { Button, Field, Heading, Hint, Input } from "@lumberset/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { signupStatusQuery } from "../../lib/queries";

interface SignupSearch {
  kind?: "builder";
  /** Back to an invitation once the account exists. Only an invitation link is followed. */
  redirect?: string;
}

/**
 * `?kind=builder` signs up a Builder: the copy changes and onboarding
 * preselects it. `?redirect=/accept-invitation/...` is someone a workspace
 * invited: they land back on the invitation, and can sign up even while an
 * Admin has sign-up closed.
 */
export const Route = createFileRoute("/_auth/signup")({
  validateSearch: (search: Record<string, unknown>): SignupSearch => ({
    ...(search.kind === "builder" ? { kind: "builder" as const } : {}),
    ...(typeof search.redirect === "string" && search.redirect.startsWith("/accept-invitation/")
      ? { redirect: search.redirect }
      : {}),
  }),
  component: Signup,
});

function Signup() {
  const router = useRouter();
  const search = Route.useSearch();
  const builder = search.kind === "builder";
  const invited = search.redirect !== undefined;
  const { data: status } = useQuery(signupStatusQuery);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);

    const onboarding = builder ? "/onboarding?kind=builder" : "/onboarding";
    const result = await auth.signUp.email({
      name: String(form.get("name")).trim(),
      email: String(form.get("email")).trim(),
      password: String(form.get("password")),
      // Where a verified email lands: the invitation, or onboarding for the right kind of workspace.
      callbackURL: search.redirect ?? onboarding,
    });

    if (result.error) {
      setBusy(false);
      setError(result.error.message ?? "We could not create that account.");
      return;
    }
    if (search.redirect) {
      await router.invalidate();
      router.history.push(search.redirect);
      return;
    }
    // Straight to onboarding. Invalidating first would re-run this layout's guard,
    // which sends a signed-in person home and loses the kind on the way.
    await router.navigate({ to: "/onboarding", search: builder ? { kind: "builder" } : {} });
  }

  // Closed by an Admin: said plainly, with the way in for someone who was invited.
  if (status?.open === false && !invited) {
    return (
      <div className="flex flex-col gap-y-5">
        <div>
          <Heading>Sign-up is closed for now</Heading>
          <p className="txt-small mt-1 text-pretty text-ui-fg-subtle">
            We are not opening new accounts at the moment. If a team invited you to LumberSet, open
            the link in your invitation email to create your account.
          </p>
        </div>
        <Button asChild size="large" variant="secondary" block>
          <Link to="/login" search={{ redirect: undefined }}>
            Sign in
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-y-5">
      <div>
        <Heading>
          {invited
            ? "Create your account"
            : builder
              ? "Know your lumber number before you bid"
              : "Create your account"}
        </Heading>
        <p className="txt-small mt-1 text-pretty text-ui-fg-subtle">
          {invited
            ? "Then you can join the team that invited you. Use the email address the invitation was sent to."
            : "Your first takeoff is free. No card needed to start."}
        </p>
      </div>

      {builder && !invited ? (
        <ul className="txt-small flex flex-col gap-y-1.5 text-ui-fg-subtle">
          <li>Upload the drawings, get back a takeoff our Reviewers have checked.</li>
          <li>A budget estimate from our reference price book, before any yard has quoted.</li>
          <li>Send the takeoff to the yards you choose and compare their quotes side by side.</li>
        </ul>
      ) : null}

      <Field label="Your name">
        {(control) => (
          <Input {...control} name="name" autoComplete="name" required size="large" autoFocus />
        )}
      </Field>

      <Field label={invited ? "Email" : "Work email"}>
        {(control) => (
          <Input
            {...control}
            name="email"
            type="email"
            autoComplete="email"
            required
            size="large"
          />
        )}
      </Field>

      <Field label="Password" hint="At least 10 characters.">
        {(control) => (
          <Input
            {...control}
            name="password"
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            size="large"
          />
        )}
      </Field>

      {error ? <Hint variant="error">{error}</Hint> : null}

      <Button type="submit" size="large" block isLoading={busy}>
        Create account
      </Button>

      <p className="txt-small text-center text-ui-fg-subtle">
        Already have an account?{" "}
        <Link
          to="/login"
          search={{ redirect: search.redirect }}
          className="text-ui-fg-interactive hover:text-ui-fg-interactive-hover"
        >
          Sign in
        </Link>
      </p>
    </form>
  );
}
